import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {css} from '@emotion/css';
import moment from 'moment';

import Map from "../Map/Map";
import IconTool from '../IconTool/PointPanel/IconTool';
import ResultPanel from '../ResultPanel/ResultPanel';
import LayerOverview from '../Legend/LayerOverview';
import PointLayerMenu from '../IconTool/LayerPanel/PointLayerMenu';
import {DeleteConfirmationModal, SessionExpirationModal} from "../../common/Modals/MainMap/MainMapModal";

import {useInitStaticLayers} from "../../../queries/useStaticLayerQuery";
import {useInitPoint} from "../../../queries/usePointsQuery";
import {useMeshLayerQuery} from "../../../queries/useMeshLayerQuery";
import {useSessionManager} from "../../../hooks/useSessionManager";
import {useMapPopups} from "../Map/hooks/useMapPopups";
import {useMapLayers} from "../Map/hooks/useMapLayers";

import {useMarkerStore} from "../../../store/markerStore";
import useLegendStore from "../../../store/legendStore";
import useHomeStore from "../../../store/homeStore";
import useDatasetStore from "../../../store/datasetStore";
import useAuthStore from "../../../store/authStore";
import useMapStore from "../../../store/mapStore";
import {useMapLayerStore} from "../../../store/layerStore";

import LoadingContext from '../../../context/loadingContext/LoadingContext';
import {withLoading} from "../../common/Loading/withLoading";

import {MarkerItem, MarkerItemToRestore} from '../../../types/home';
import {IDatasetBrief} from '../../../types/metadata';
import {MARKERS_TO_RESTORE} from '../../../utils/StaticVariable';
import {SEARCHPARAM} from "../../../utils/common";
import {surfaceMaps} from "../../../types/marker";

import styles from './AppContainer.module.scss';
import useCartStore from "../../../store/cartStore";

export default function AppContainer() {
  const user = useAuthStore(state => state.user);

  const showMesh = useLegendStore(state => state.showMesh);

  const datasetBriefs = useDatasetStore(state => state.datasetBriefs);

  const datasetZones = useMapLayerStore(state => state.datasetZones);

  const shoppingItems = useCartStore(state => state.shoppingItems);

  const coordsMoved = useMapStore(state => state.coordsMoved);
  const setCoordsMoved = useMapStore(state => state.setCoordsMoved);
  const setMapCenter = useMapStore(state => state.setMapCenter);

  const showResultCollapse = useHomeStore(state => state.showResultCollapse);
  const setShowResultCollapse = useHomeStore(state => state.setShowResultCollapse);
  const setOverridePointData = useHomeStore(state => state.setOverridePointData);

  const markers = useMarkerStore(state => state.markers);
  const activeMarker = useMarkerStore(state => state.activeMarker);
  const setActiveMarker = useMarkerStore(state => state.setActiveMarker);
  const updateMarker = useMarkerStore(state => state.updateMarker);
  const restoreMarkers = useMarkerStore(state => state.restoreMarkers);
  const isAddMarkerMode = useMarkerStore(state => state.isAddMarkerMode);
  const setAddMarkerMode = useMarkerStore(state => state.setAddMarkerMode);

  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [legendStartEndValues, setLegendStartEndValues] = useState<[number | null, number | null]>([null, null]);
  const [checkDataToRestore, setCheckDataToRestore] = useState<boolean>(false);

  const loadingContext = useContext(LoadingContext);
  const { isLoading } = loadingContext;
  const location = useLocation();
  const didMountRef = useRef(false);
  const point = activeMarker;

  useInitStaticLayers();
  useInitPoint();
  const {
    meshUrl,
    setMeshUrl,
    meshResults,
    setMeshResults
  } = useMeshLayerQuery(selectedDatasetId, showMesh);

  const { isModalOpen, handleLogin } = useSessionManager(user);
  const {
    confirmationDeletePopup,
    setConfirmationDeletePopup,
    notAuthorizedUserPopup,
    setNotAuthorizedUserPopup,
    confirmationDeletePopupInsitu,
    setConfirmationDeletePopupInsitu,
    loadingDelete,
    expandedPoints,
    handleDeleteDataset,
    handleDeleteDatasetInsitu,
    handleRequestDeleteDataset,
    handlePopupCoordinatesChanged,
    handleClosePopup,
    popupContent,
    activePopupCoordinates,
    triggerMapClick
  } = useMapPopups(shoppingItems);

  const { layer } = useMapLayers(meshUrl, setMeshUrl, expandedPoints);

  const toggleResultCollapse = useCallback((bool: boolean, _checked: boolean = true) => {
    setShowResultCollapse(!bool);
  }, [setShowResultCollapse]);

  const onResetCoordsMoved = useCallback(() => {
    setCoordsMoved(false);
    if (point) {
      updateMarker(point.id, {coordinatesToMove: undefined});
    }
  }, [point, setCoordsMoved, updateMarker]);

  const getAcronym = useCallback(() => {
    if (!point?.dataset || !datasetBriefs?.length) return undefined;

    const brief = datasetBriefs.find(
      (datasetBrief: IDatasetBrief) => datasetBrief.Id === point.dataset
    );
    return brief?.Acronym;
  }, [point?.dataset, datasetBriefs]);

  const onAddCoords = useCallback((coords: number[], fwePointName?: string) => {
    withLoading(() => triggerMapClick(coords, fwePointName), loadingContext, {
      source: 'onAddCoords',
    });
  }, [triggerMapClick, loadingContext]);

  const resolvedPopupContent = useMemo(() => {
    if (!activePopupCoordinates()) return null;
    return popupContent();
  }, [popupContent, activePopupCoordinates]);

  const activeCoordinates = useMemo(() => {
    return activePopupCoordinates();
  }, [activePopupCoordinates]);

  // Handle data restoration from localStorage
  useEffect(() => {
    if (!checkDataToRestore || isLoading) return;

    const itemToRestore = localStorage.getItem(MARKERS_TO_RESTORE);
    if (itemToRestore) {
      localStorage.removeItem(MARKERS_TO_RESTORE);
      setOverridePointData(false);

      try {
        const parsedMarkers: MarkerItemToRestore[] = JSON.parse(itemToRestore);
        const markersToSave = parsedMarkers.map(x => {
          const temp: MarkerItem = {
            id: x.id,
            label: x.label,
            coordinates: x.coordinates,
            active: x.active,
            depth: 0,
            dataset: '',
            allDataset: {} as any,
            downloadPage: x.downloadPage.map(d => ({
              datasetId: d.datasetId,
              radioKey: d.radioKey,
              dateRange: d.dateRange && [moment(d.dateRange[0]), moment(d.dateRange[1])],
              options: []
            })),
            shapeFiles: [],
            surfaceMaps: {
              enable: false,
              overlays: [],
              selectedLevel1: '',
              selectedLevel2: '',
              categories: [],
            } as surfaceMaps,
            fwePointName: x.fwePointName
          };
          return temp;
        });
        setShowResultCollapse(markersToSave.length > 0)
        restoreMarkers(markersToSave);
      } catch (error) {
        console.error('Error restoring markers:', error);
      }
    }

    setCheckDataToRestore(false);
  }, [checkDataToRestore, isLoading, restoreMarkers, setOverridePointData]);

  // Update result panel collapse based on markers or add marker mode
  useEffect(() => {
    setShowResultCollapse(isAddMarkerMode || (markers?.length > 0));
  }, [markers?.length, isAddMarkerMode, setShowResultCollapse]);

  // Handle URL parameters for initial coordinates
  useEffect(() => {
    if (didMountRef.current || !datasetBriefs.length) return;

    const searchParams = new URLSearchParams(location.search);
    const coordinate = searchParams.get(SEARCHPARAM.COOR);

    if (coordinate) {
      const fwePointName = searchParams.get(SEARCHPARAM.FWEPOINTNAME);
      let decodedCoor = decodeURIComponent(coordinate);

      // Handle double encoding
      if (decodeURIComponent(decodedCoor) === decodedCoor) {
        decodedCoor = decodeURIComponent(decodedCoor);
      }

      try {
        const [longitude, latitude] = JSON.parse(decodedCoor);
        if (!isNaN(longitude) && !isNaN(latitude)) {
          setMapCenter({ longitude, latitude, zoom: 4 });
          onAddCoords(JSON.parse(decodedCoor), fwePointName || undefined);
        }
      } catch (error) {
        console.error('Error parsing coordinates:', error);
      }
    }

    didMountRef.current = true;
  }, [datasetBriefs, location.search, onAddCoords, setMapCenter]);

  return (
    <div className={styles.map}>
      {datasetZones?.length > 0 && (
        <PointLayerMenu>
          <LayerOverview
            legendStartEndValues={legendStartEndValues}
            setLegendStartEndValues={setLegendStartEndValues}
            onMeshControl={setSelectedDatasetId}
            result={meshResults}
            setResult={setMeshResults} />
        </PointLayerMenu>
      )}

      <IconTool
        legendStartEndValues={legendStartEndValues}
        setLegendStartEndValues={setLegendStartEndValues}
        collapseOpen={!showResultCollapse}
        onCollapse={toggleResultCollapse}
        result={meshResults}
        setResult={setMeshResults}
      />

      {point?.dataset && (
        <div className={acronymStyle(showResultCollapse)}>{getAcronym()}</div>
      )}

      <ResultPanel
        coordsMoved={coordsMoved}
        onResetCoordsMove={onResetCoordsMoved}
        isMark={isAddMarkerMode}
        open={showResultCollapse}
        onMeshControl={setSelectedDatasetId}
        addCoords={onAddCoords}
        onMark={setAddMarkerMode}
        setMeshUrl={setMeshUrl}
      />

      <Map
        onClick={triggerMapClick}
        layers={layer}
        popupContent={resolvedPopupContent}
        popupCoordinates={activeCoordinates}
        onPopupCoordinatesChanged={handlePopupCoordinatesChanged}
        onClosePopup={handleClosePopup}
      />

      <DeleteConfirmationModal
        visible={confirmationDeletePopup}
        onConfirm={handleDeleteDataset}
        onCancel={() => setConfirmationDeletePopup(false)}
        loading={loadingDelete}
        message="On confirmation the dataset will be removed from MOOD"
      />

      <DeleteConfirmationModal
        visible={notAuthorizedUserPopup}
        onConfirm={handleRequestDeleteDataset}
        onCancel={() => setNotAuthorizedUserPopup(false)}
        loading={loadingDelete}
        message="MIKE MS datasets can only be removed by the project owners, request sent to owners"
      />

      <DeleteConfirmationModal
        visible={confirmationDeletePopupInsitu}
        onConfirm={handleDeleteDatasetInsitu}
        onCancel={() => setConfirmationDeletePopupInsitu(false)}
        loading={loadingDelete}
        message="On confirmation the dataset will be removed from MOOD"
      />

      <SessionExpirationModal
        visible={isModalOpen}
        onLogin={handleLogin}
      />
    </div>
  );
}

const acronymStyle = (sidePanelOpen: boolean) => {
  const right = sidePanelOpen ? '566px' : '16px';
  return css`
      padding: 8px 12px;
      text-align: center;
      position: absolute;
      bottom: 48px;
      color: #ffffff;
      background-color: #00A4EC;
      z-index: 5;
      display: block;
      right: ${right};
    `;
}
